import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiTarget, FiEye, FiActivity, FiAward, FiCheckSquare, FiShield, FiAnchor, FiTrendingUp } from 'react-icons/fi';
import api from '../utils/api';

const values = [
  { icon: '🎯', title: 'Integrity', desc: 'Unwavering honesty and transparency in every client engagement.' },
  { icon: '⭐', title: 'Excellence', desc: 'Exceptional quality delivered consistently across all our services.' },
  { icon: '💡', title: 'Innovation', desc: 'Modern approaches and best practices in food safety consulting.' },
  { icon: '🤝', title: 'Partnership', desc: 'Building lasting relationships by treating every client success as our own.' },
];
const timeline = [
  { year: '2008', title: 'Founded in Accra', desc: 'SQS established with a mission to elevate food safety standards across Ghana.' },
  { year: '2012', title: 'Regional Expansion', desc: 'Extended services to cover West Africa, supporting multinationals and SMEs.' },
  { year: '2016', title: 'ISO Accreditation', desc: 'Became one of Ghana\'s first ISO-accredited food safety consulting firms.' },
  { year: '2020', title: 'Digital Transformation', desc: 'Launched digital audit tools and online training programmes.' },
  { year: '2024', title: '500+ Clients', desc: 'Reached the milestone of 500+ satisfied clients with a 98% retention rate.' },
];

export default function About() {
  const [team, setTeam] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get('/team').then(r => setTeam(r.data)).catch(() => {});
    api.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div style={{ paddingTop: 74 }}>
      <section style={{ background: 'linear-gradient(135deg, #060F1E 0%, var(--navy) 100%)', padding: '5.5rem 0 4rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 500, height: 500, borderRadius: '50%', background: 'rgba(201,168,76,0.04)' }} />
        <div className="container">
          <span className="label" style={{ marginBottom: '1rem', display: 'inline-flex' }}>Our Story</span>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(2.2rem, 4vw, 3.2rem)', color: 'white', marginBottom: '1.25rem', maxWidth: 600, lineHeight: 1.15 }}>
            Built on Expertise, Driven by Purpose
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.05rem', maxWidth: 520, lineHeight: 1.75 }}>
            Over 10 years of delivering measurable food safety improvements across West Africa.
          </p>
        </div>
      </section>

      {/* ══ ABOUT / WHO WE ARE ═══════════════════════════════ */}
            <section className="section-pad section-ivory">
              <div className="container">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5rem', alignItems:'center' }} className="about-grid">
                  {/* Image grid */}
                  <motion.div initial={{ opacity:0, x:-30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.875rem' }}>
                      <div style={{ gridRow:'1/3', borderRadius:'var(--radius-lg)', overflow:'hidden', height:400, boxShadow:'var(--shadow-lg)' }}>
                        <img src="/hero1.png" alt="About" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      </div>
                      <div style={{ borderRadius:'var(--radius-lg)', overflow:'hidden', height:194, boxShadow:'var(--shadow-md)' }}>
                        <img src="/hero3.png" alt="Team" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      </div>
                      <div style={{ borderRadius:'var(--radius-lg)', background:'var(--navy)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:194, gap:'0.5rem', boxShadow:'var(--shadow-md)' }}>
                        <FiAward style={{ fontSize:'2rem', color:'var(--gold-light)' }} />
                        <div style={{ fontWeight:900, fontSize:'2.2rem', color:'var(--white)', lineHeight:1 }}>15+</div>
                        <div style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.7)', fontWeight:500 }}>Years Excellence</div>
                      </div>
                    </div>
                  </motion.div>
      
                  {/* Text */}
                  <motion.div initial={{ opacity:0, x:30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}>
                    <div style={{ display:'inline-flex', alignItems:'center', background:'var(--gold-pale)', border:'1px solid rgba(184,146,42,0.3)', borderRadius:'var(--radius-sm)', padding:'0.35rem 0.9rem', marginBottom:'1.25rem' }}>
                      <span style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--gold)' }}>Who We Are</span>
                    </div>
                    <h2 className="section-heading" style={{ marginBottom:'0.75rem' }}>
                      Crafting Tomorrow's<br />Food Safety Standards
                    </h2>
                    <div className="gold-bar" />
                    <p style={{ color:'var(--txt-muted)', lineHeight:1.85, marginBottom:'2rem', fontSize:'0.975rem' }}>
                      {settings.about_story || 'Smart Quality Systems was founded with a singular vision: to make world-class food safety consulting accessible to every food business in Ghana and West Africa. With over 15 years of expertise, we have helped 500+ businesses achieve compliance and operational excellence.'}
                    </p>
                    {['Certified professionals with deep, hands-on food industry experience.',
                      'Tailored consulting packages designed for your specific operations.',
                      'Continuous monitoring to keep you compliant and growing year-round.'].map((pt,i)=>(
                      <div key={i} style={{ display:'flex', gap:'0.875rem', padding:'0.875rem 0', borderBottom:'1px solid var(--border)', alignItems:'flex-start' }}>
                        <span style={{ fontWeight:900, fontSize:'0.9rem', color:'var(--gold)', minWidth:26, flexShrink:0, marginTop:1 }}>{String(i+1).padStart(2,'0')}</span>
                        <span style={{ fontSize:'0.9rem', color:'var(--txt-body)', fontWeight:500, lineHeight:1.55 }}>{pt}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', gap:'0.875rem', marginTop:'2.25rem', flexWrap:'wrap' }}>
                      <Link to="/about" className="btn btn-primary" style={{ textDecoration:'none' }}>
                        Learn More <FiArrowRight />
                      </Link>
                      <Link to="/book-service" className="btn btn-outline-gold" style={{ textDecoration:'none' }}>
                        Book Consultation
                      </Link>
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>

      {/* Mission & Vision */}
      <section className="section-pad" style={{ background: 'var(--off-white)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span className="label" style={{ marginBottom: '0.75rem', display: 'inline-flex', justifyContent: 'center' }}>Our Purpose</span>
            <h2 className="section-title">Mission & Vision</h2>
            <div className="gold-line" style={{ margin: '1rem auto 0' }} />
          </div>
          <div className="grid-2">
            {[
              {
                label: 'Our Mission',
                icon: <FiTarget style={{ fontSize: '2rem', color: '#C9A84C' }} />,
                content: settings.mission || 'Our Mission is to provide effective, competitive, result oriented & practical advisory solutions to both public and private sector organizations',
                accent: '#0B2545',
              },
              {
                label: 'Our Vision',
                icon: <FiEye style={{ fontSize: '2rem', color: '#C9A84C' }} />,
                content: settings.vision || 'Our vision is to become the preferred Regulatory service providers, ISO Management training and certification firm, and R&D service providers in Africa and beyond.',
                accent: '#13315C',
              },
            ].map((item, i) => (
              <motion.div key={item.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                style={{ background: 'white', borderRadius: '20px', padding: '2.5rem', boxShadow: 'var(--shadow)', border: '1px solid var(--gray-100)', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ width: 60, height: 60, borderRadius: '16px', background: 'var(--off-white)', border: '1.5px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{ width: 4, height: 28, background: '#C9A84C', borderRadius: '2px' }} />
                    <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.25rem', color: 'var(--navy)' }}>{item.label}</h3>
                  </div>
                  <p style={{ color: 'var(--gray-500)', lineHeight: 1.85, fontSize: '0.96rem' }}>{item.content}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SMART Acronym */}
      <section className="section-pad">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span className="label" style={{ marginBottom: '0.75rem', display: 'inline-flex', justifyContent: 'center' }}>What We Stand For</span>
            <h2 className="section-title">The S.M.A.R.T. Difference</h2>
            <div className="gold-line" style={{ margin: '1rem auto 0' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 860, margin: '0 auto' }}>
            {[
              { letter: 'S', word: 'Specific', detail: 'Every recommendation we make is grounded in evidence-based food science and internationally recognised standards.', icon: <FiActivity /> },
              { letter: 'M', word: 'Measurable', detail: 'We leave nothing to chance. Our audits, assessments and reports are thorough, precise and detail-oriented.', icon: <FiCheckSquare /> },
              { letter: 'A', word: 'Achievable', detail: 'We take full ownership of our work and stand behind every recommendation, report and certification we issue.', icon: <FiShield /> },
              { letter: 'R', word: 'Realistic', detail: 'Our clients trust us to deliver consistently — on time, to standard and with clear communication throughout.', icon: <FiAnchor /> },
              { letter: 'T', word: 'Time Bound', detail: 'We do not just tick compliance boxes. We help businesses build lasting food safety cultures that drive real improvement.', icon: <FiTrendingUp /> },
            ].map((item, i) => (
              <motion.div key={item.letter} initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                style={{ display: 'flex', alignItems: 'center', gap: '1.75rem', background: 'white', borderRadius: '16px', padding: '1.5rem 2rem', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)', transition: 'all 0.3s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateX(6px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-100)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; }}>
                {/* Letter */}
                <div style={{ width: 64, height: 64, borderRadius: '16px', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: '2rem', color: '#C9A84C', lineHeight: 1 }}>{item.letter}</span>
                </div>
                {/* Icon */}
                <div style={{ width: 44, height: 44, borderRadius: '12px', background: 'var(--off-white)', border: '1.5px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--navy)', fontSize: '1.1rem' }}>
                  {item.icon}
                </div>
                {/* Text */}
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.1rem', color: 'var(--navy)', marginBottom: '0.35rem' }}>{item.word}</h4>
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', lineHeight: 1.7 }}>{item.detail}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      
        {/* Team */}
      {team.length > 0 && (
        <section className="section-pad" style={{ background: 'var(--navy)' }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
              <span className="label" style={{ marginBottom: '0.75rem', display: 'inline-flex', justifyContent: 'center' }}>The People</span>
              <h2 className="section-title" style={{ color: 'white' }}>Meet Our Expert Team</h2>
              <div className="gold-line" style={{ margin: '1rem auto 0' }} />
            </div>
            <div className="grid-4">
              {team.map((m, i) => (
                <motion.div key={m.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  style={{ textAlign: 'center', padding: '2rem 1.5rem', borderRadius: '16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', transition: 'all 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}>
                  {m.photo ? <img src={`${BACKEND}${m.photo}`} alt={m.name} style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 1rem', border: '3px solid var(--gold)' }} />
                    : <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'var(--navy)', margin: '0 auto 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Fraunces, serif', fontSize: '2rem', fontWeight: 700, color: '#C9A84C' }}>{m.name?.[0]}</div>}
                  <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', color: 'white', marginBottom: '0.25rem' }}>{m.name}</h3>
                  <p style={{ color: '#C9A84C', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>{m.position}</p>
                  {m.bio && <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{m.bio}</p>}

                  {/* Social icons */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                    {m.email && (
                      <a href={`mailto:${m.email}`} title="Email" style={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A84C', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.25)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      </a>
                    )}
                    {m.linkedin && (
                      <a href={m.linkedin} target="_blank" rel="noreferrer" title="LinkedIn" style={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A84C', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.25)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                      </a>
                    )}
                    {m.twitter && (
                      <a href={m.twitter} target="_blank" rel="noreferrer" title="Twitter / X" style={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A84C', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.25)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
